import React, { useState } from 'react';
import { useConfigStore } from '@stores/configStore';
import { Globe, AlertCircle, CheckCircle } from 'lucide-react';
import type { AISAdminPanelConfig } from '../types/config';

interface AISAdminPanelFormProps {
  onTestConnection?: () => Promise<void>;
  testResult?: { success: boolean; message: string };
}

const PLATFORM_CODES = [
  { value: 'ORCL-EBS', label: 'Oracle E-Business Suite' },
  { value: 'ORCL-FUSION', label: 'Oracle Fusion Cloud ERP' },
  { value: 'ORCL-NETSUITE', label: 'Oracle NetSuite' },
  { value: 'ORCL-JDE', label: 'Oracle JD Edwards' },
  { value: 'ORCL-PSFT', label: 'Oracle PeopleSoft' },
];

export const AISAdminPanelForm: React.FC<AISAdminPanelFormProps> = ({ onTestConnection, testResult }) => {
  const { config, updateAISAdminPanel } = useConfigStore();
  const ais = config.aisAdminPanel;
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const handleUpdate = (updates: Partial<AISAdminPanelConfig>) => {
    updateAISAdminPanel(updates);
  };

  const handleRetryPolicyUpdate = (key: keyof AISAdminPanelConfig['retryPolicy'], value: number) => {
    handleUpdate({
      retryPolicy: {
        ...ais.retryPolicy,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">AIS Admin Panel Integration</h3>
      </div>

      {/* Basic Configuration */}
      <div className="border-t pt-4">
        <h4 className="font-semibold text-gray-900 mb-4">Connection Settings</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Panel Base URL</label>
            <input
              type="url"
              value={ais.baseUrl}
              onChange={(e) => handleUpdate({ baseUrl: e.target.value })}
              placeholder="https://ais-admin.yourdomain.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">The base URL of the AIS Admin Panel API</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <input
                type="password"
                value={ais.apiKey}
                onChange={(e) => handleUpdate({ apiKey: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
              <input
                type="password"
                value={ais.apiSecret}
                onChange={(e) => handleUpdate({ apiSecret: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform Code</label>
            <select
              value={ais.platformCode}
              onChange={(e) => handleUpdate({ platformCode: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {PLATFORM_CODES.map((platform) => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Select the Oracle platform you are integrating with</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Version Control</label>
            <input
              type="text"
              value={ais.versionControl}
              onChange={(e) => handleUpdate({ versionControl: e.target.value })}
              placeholder="1.0.0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">API version for compatibility tracking</p>
          </div>
        </div>
      </div>

      {/* Retry Policy */}
      <div className="border-t pt-4">
        <h4 className="font-semibold text-gray-900 mb-4">Retry Policy</h4>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Retries</label>
            <input
              type="number"
              value={ais.retryPolicy.maxRetries}
              onChange={(e) => handleRetryPolicyUpdate('maxRetries', parseInt(e.target.value))}
              min="0"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Retry Delay (ms)</label>
            <input
              type="number"
              value={ais.retryPolicy.retryDelay}
              onChange={(e) => handleRetryPolicyUpdate('retryDelay', parseInt(e.target.value))}
              min="100"
              step="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Backoff Multiplier</label>
            <input
              type="number"
              value={ais.retryPolicy.backoffMultiplier}
              onChange={(e) => handleRetryPolicyUpdate('backoffMultiplier', parseFloat(e.target.value))}
              min="1"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">Configure retry behavior for failed API calls</p>
      </div>

      {/* Webhook Configuration */}
      <div className="border-t pt-4">
        <h4 className="font-semibold text-gray-900 mb-4">Webhook Configuration</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
            <input
              type="url"
              value={ais.webhookUrl}
              onChange={(e) => handleUpdate({ webhookUrl: e.target.value })}
              placeholder="https://yourdomain.com/webhook"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">URL where AIS will send webhook notifications</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Secret</label>
            <div className="relative">
              <input
                type={showWebhookSecret ? 'text' : 'password'}
                value={ais.webhookSecret}
                onChange={(e) => handleUpdate({ webhookSecret: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
              >
                {showWebhookSecret ? '🙈' : '👁️'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Secret key for webhook signature verification</p>
          </div>
        </div>
      </div>

      {/* Error Message Templates */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="errorTemplates"
            checked={ais.errorMessageTemplates}
            onChange={(e) => handleUpdate({ errorMessageTemplates: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="errorTemplates" className="text-sm font-medium text-gray-700">
            Use Error Message Templates
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">Enable formatted error messages from AIS API responses</p>
      </div>

      {/* Test Connection */}
      {onTestConnection && (
        <button
          onClick={onTestConnection}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Test AIS Connection
        </button>
      )}

      {testResult && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>{testResult.message}</p>
        </div>
      )}
    </div>
  );
};
